import { takeLatest, call, fork, put, select, take } from 'redux-saga/effects';
import queryString from 'query-string';
import { isString } from 'lodash-es';
import i18n from '@/../i18n';
import { ContactsPaths } from '@/services/dictionaries/Paths';
import {
    GetMailchimpConnectionParamsAction,
    IntegrationsActionTypes,
    MailchimpConnectionParams,
    GetMailchimpListsAction,
    MailchimpPaginationParams,
    GetMailchimpListMembersAction,
} from '@/store/integrations/types';
import { GetContactsPatternStart } from '@/store/contacts/actions';
import { UploadFiles } from '@/store/upload/actions';
import { getMailchimpAccessData, getMailchimpApiData } from '@/services/ApiCategories/integration';
import { getConfigStore } from '@/store';
import { Logger } from '@/services/errors/appLogger';
import {
    GetMailchimpLists,
    SetMailchimpConnectionParams,
    SetMailchimpListMembersProgress,
    SetMailchimpLists,
    getIntegrationsState,
} from '../store/integrations/actions';
import { ShowToast, HideToast } from '../store/toast/actions';

const LISTS_PAGE_SIZE = 1000;
const MEMBERS_PAGE_SIZE = 1000;

const errorToast = (e): any =>
    ShowToast({
        severity: 'error',
        message: `Error: ${e.response?.data?.Error ?? e.message}`,
        duration: 6000,
        onClose: HideToast,
    });

const getMailchimpDetails = async (code: string, baseName: string): Promise<MailchimpConnectionParams> => {
    try {
        //FIXME: change to /marketing to be condition depended if its standalone or not
        const redirectUri = `${location.origin}${baseName}${ContactsPaths.ContactsUploadImportMailchimp}`;
        const response = await getMailchimpAccessData(
            code,
            MAILCHIMP_CLIENT_ID,
            MAILCHIMP_CLIENT_SECRET || null,
            redirectUri
        );
        return { dc: response.data.DC, accessToken: response.data.AccessToken, hasError: false, tempCode: code };
    } catch (e) {
        Logger.error(e);
        return { dc: '', accessToken: '', hasError: true, tempCode: null };
    }
};

const getLists = async (
    mailchimpParams: MailchimpConnectionParams,
    pagination: MailchimpPaginationParams
): Promise<any> => {
    if (!mailchimpParams) {
        return;
    }
    const params = {
        count: pagination.count,
        offset: pagination.offset,
        sort_field: 'date_created',
        sort_dir: 'DESC',
        include_total_contacts: true,
    };
    const path = `/3.0/lists?${queryString.stringify(params)}`;
    const list = await getMailchimpApiData(mailchimpParams.accessToken, mailchimpParams.dc, path, {}, 'GET');

    return isString(list.data) ? JSON.parse(list.data) : list.data;
};

const getMembers = async (
    mailchimpParams: MailchimpConnectionParams,
    id: string,
    pagination: MailchimpPaginationParams
): Promise<any> => {
    const params = {
        status: 'subscribed',
        count: pagination.count,
        offset: pagination.offset,
    };
    const path = `/3.0/lists/${id}/members?${queryString.stringify(params)}`;
    const members = await getMailchimpApiData(mailchimpParams.accessToken, mailchimpParams.dc, path, {}, 'GET');

    return isString(members.data) ? JSON.parse(members.data) : members.data;
};

function* getMailchimpConnectionParams(action: GetMailchimpConnectionParamsAction) {
    const { tempCode } = action.payload;
    const { routerBasePath = '' } = yield select(getConfigStore);

    try {
        const params = yield call(getMailchimpDetails, tempCode, routerBasePath);
        yield put(SetMailchimpConnectionParams(params));
        if (params.accessToken && params.dc) {
            yield put(GetMailchimpLists({ offset: 0, count: LISTS_PAGE_SIZE }));
        } else {
            throw new Error(i18n.t('errorinfo'));
        }
    } catch (e) {
        Logger.error(e);
        yield put(errorToast(e));
    }
}

function* getMailchimpLists(action: GetMailchimpListsAction) {
    const paginationParams = action.payload;
    const { mailchimp } = yield select(getIntegrationsState);
    try {
        const listsResponse = yield call(getLists, mailchimp.connection, paginationParams);
        yield put(
            SetMailchimpLists({
                data: listsResponse.lists,
                paginationParams,
                total: listsResponse.total_items,
            })
        );
    } catch (e) {
        Logger.error(e);
        yield put(errorToast(e));
    }
}

interface MailchimpMember {
    email_address: string;
    merge_fields: {
        FNAME: string;
        LNAME: string;
        ADDRESS: {
            addr1?: string;
            addr2?: string;
            city?: string;
            country?: string;
            state?: string;
            zip?: string;
        };
        BIRTHDAY?: string;
        PHONE?: string;
    } & Record<string, string>;
}

const memberMap = (member: MailchimpMember) => {
    const { FNAME, LNAME, ADDRESS, BIRTHDAY, PHONE, ...mergeFields } = member.merge_fields;
    return {
        email: member.email_address,
        firstname: FNAME,
        lastname: LNAME,
        birthday: BIRTHDAY || '',
        phone: PHONE || '',
        street: ADDRESS?.addr1 || '',
        street2: ADDRESS?.addr2 || '',
        city: ADDRESS?.city || '',
        state: ADDRESS?.state || '',
        country: ADDRESS?.country || '',
        zip: ADDRESS?.zip || '',
        ...mergeFields,
    };
};

function* getMailchimpListMembers(action: GetMailchimpListMembersAction) {
    const { listId } = action.payload;
    const { mailchimp } = yield select(getIntegrationsState);
    try {
        const count = MEMBERS_PAGE_SIZE;
        const members = [];
        const startTimestamp = Date.now();
        yield put(SetMailchimpListMembersProgress(1));
        const membersResponse = yield call(getMembers, mailchimp.connection, listId, { offset: 0, count });
        members.push(...membersResponse.members.map(memberMap));
        const totalPages = Math.floor(membersResponse.total_items / membersResponse.members.length);
        const totalProgressSteps = totalPages + 1;
        yield put(SetMailchimpListMembersProgress(Math.floor((1 / totalProgressSteps) * 100)));
        for (let i = 1; i <= totalPages; i++) {
            const membersResponse = yield call(getMembers, mailchimp.connection, listId, { offset: i * count, count });
            members.push(...membersResponse.members.map(memberMap));
            yield put(SetMailchimpListMembersProgress(Math.floor(((1 + i) / totalProgressSteps) * 100)));
        }
        const fileName = `mailchimp-${startTimestamp}-${Date.now()}.json`;

        const blob = new Blob([JSON.stringify(members)], { type: 'application/json' });
        const file = new File([blob], fileName);

        yield put(UploadFiles(file, 1));

        yield take('@@userFiles/LOADING_FILES_SUCCESS');

        yield put(SetMailchimpListMembersProgress(99));

        yield put(
            GetContactsPatternStart({
                fileName,
                encodingName: 'utf-8',
            })
        );

        yield put(SetMailchimpListMembersProgress(100));
    } catch (e) {
        Logger.error(e);
        yield put(SetMailchimpListMembersProgress(0));
        yield put(errorToast(e));
    }
}

function* watchMailchimpConnection(): any {
    yield takeLatest(IntegrationsActionTypes.GET_MAILCHIMP_CONNECTION_PARAMS, getMailchimpConnectionParams);
}

function* watchMailchimpLists(): any {
    yield takeLatest(IntegrationsActionTypes.GET_MAILCHIMP_LISTS, getMailchimpLists);
}

function* watchMailchimpListMembers(): any {
    yield takeLatest(IntegrationsActionTypes.GET_MAILCHIMP_LIST_MEMBERS, getMailchimpListMembers);
}

const integrationsSagas = [fork(watchMailchimpConnection), fork(watchMailchimpLists), fork(watchMailchimpListMembers)];

export default integrationsSagas;
